import { Label, Button, Badge } from "flowbite-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetGroupsQuery, useCreateGroupMutation } from "@/api/ticketApi/category";
import { Group } from "@/sharedTypes";
import GroupFormDialog from "./GroupFormDialog";
import { toast } from "react-toastify";
import { getErrorMessage } from "@/utils/requestUtils";
import { HiPlus, HiX } from "react-icons/hi";

interface GroupSelectProps {
  value: number[];
  onChange: (value: number[]) => void;
  error?: string;
  label?: string;
}

export default function GroupSelect({
  value,
  onChange,
  error,
  label,
}: GroupSelectProps) {
  const { t } = useTranslation(["category"]);
  const { data: groupsData, isLoading } = useGetGroupsQuery({
    pagination: { page: 1, pageSize: 100 },
  });

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createGroup] = useCreateGroupMutation();

  const handleCreateGroup = (data: Group) => {
    createGroup(data)
      .unwrap()
      .then((res: any) => {
        toast.success(t("category:groupCreated"));
        setIsCreateDialogOpen(false);
        const groupId = res?.group_id || res?.data?.group_id;
        if (groupId) {
          onChange([...(Array.isArray(value) ? value : []), groupId]);
        }
      })
      .catch((err) => {
        toast.error(getErrorMessage(err));
      });
  };

  const groups: Group[] = groupsData?.items || [];
  const safeValue = Array.isArray(value) ? value : [];

  const handleToggleGroup = (groupId: number) => {
    if (safeValue.includes(groupId)) {
      onChange(safeValue.filter((id) => id !== groupId));
    } else {
      onChange([...safeValue, groupId]);
    }
  };

  const handleRemoveGroup = (groupId: number) => {
    onChange(safeValue.filter((id) => id !== groupId));
  };

  const selectedGroups = groups.filter((g) => safeValue.includes(g.group_id));
  const availableGroups = groups.filter((g) => !safeValue.includes(g.group_id));
  const displayLabel = label || t("category:group");

  return (
    <div className="mb-4">
      <div className="mb-2 flex items-center justify-between">
        <Label value={displayLabel} />
        <Button
          size="xs"
          color="light"
          onClick={() => setIsCreateDialogOpen(true)}
          title={t("category:createGroup")}
        >
          <HiPlus className="h-4 w-4" />
        </Button>
      </div>

      {selectedGroups.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {selectedGroups.map((group) => (
            <Badge key={group.group_id} color="info" className="flex items-center gap-1">
              {group.group_name}
              <button
                type="button"
                onClick={() => handleRemoveGroup(group.group_id)}
                className="ml-1 inline-flex items-center"
              >
                <HiX className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <select
        className={`block w-full rounded-lg border ${error ? "border-red-500" : "border-gray-300"} bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-cyan-500 focus:ring-cyan-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-cyan-500 dark:focus:ring-cyan-500`}
        value=""
        size={Math.min(availableGroups.length + 1, 6)}
        onChange={(e) => {
          const groupId = Number(e.target.value);
          if (groupId) handleToggleGroup(groupId);
        }}
        disabled={isLoading || availableGroups.length === 0}
      >
        <option value="" disabled>
          {availableGroups.length === 0
            ? t("category:allGroupsSelected")
            : t("category:selectGroup")}
        </option>
        {availableGroups.map((group) => (
          <option key={group.group_id} value={group.group_id}>
            {group.group_name}
          </option>
        ))}
      </select>

      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}

      <GroupFormDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateGroup}
      />
    </div>
  );
}
